import { Fragment } from "react";
import { Check, Minus } from "lucide-react";
import { Reveal, Overline } from "@/components/primitives";
import {
  plans,
  planPricing,
  featureLabel,
  platformName,
  recommendedPlanId,
} from "@/config/brand";

const CAPABILITY_GROUPS = [
  { group: "Reputation", section: "reputation", items: ["review", "request", "employee"] },
  { group: "Visibility", section: "visibility", items: ["listing", "widget", "website"] },
  { group: "Engagement", section: "engagement", items: ["social"] },
];

const REVIEW_SITES = ["google", "yelp", "facebook", "tripadvisor", "trustpilot", "booking", "airbnb", "zillow", "bbb"];
const LISTINGS = ["google", "apple_maps"];
const SOCIAL = ["facebook", "instagram", "tiktok", "linkedin"];

const hasFeature = (plan, section, item) =>
  (plan[section]?.features?.enabled || []).includes(item);
const hasSite = (plan, site) =>
  (plan.reputation?.review_sites?.enabled || []).includes(site);
const hasListing = (plan, l) =>
  (plan.visibility?.listings?.enabled || []).includes(l);
const hasSocial = (plan, s) =>
  (plan.engagement?.social_platforms?.enabled || []).includes(s);

const Cell = ({ on }) => (
  <td className="px-4 py-3.5 text-center align-middle">
    {on ? (
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-black">
        <Check size={12} className="text-white" />
      </span>
    ) : (
      <Minus size={16} className="mx-auto text-black/20" />
    )}
  </td>
);

const GroupRow = ({ label }) => (
  <tr>
    <td
      colSpan={1 + plans.length}
      className="bg-base px-4 pb-2 pt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-black/45"
    >
      {label}
    </td>
  </tr>
);

const FeatureRow = ({ label, values }) => (
  <tr className="border-t border-black/[0.07]">
    <td className="px-4 py-3.5 text-sm font-medium text-black/80">{label}</td>
    {values.map((on, i) => (
      <Cell key={i} on={on} />
    ))}
  </tr>
);

export default function ComparisonTable() {
  return (
    <section className="bg-base py-24 lg:py-32">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>Full breakdown</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter text-black sm:text-5xl lg:text-6xl">
              Compare every plan
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-black/60">
              A clear, side-by-side look at exactly what's included — so you can pick with confidence.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div
            data-testid="comparison-table"
            className="mt-14 overflow-x-auto rounded-[10px] border border-black/10 bg-white"
          >
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr>
                  <th className="w-[34%] px-4 py-6 text-left align-bottom">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-black/45">
                      What's included
                    </span>
                  </th>
                  {plans.map((plan) => {
                    const pricing = planPricing(plan);
                    const recommended = plan.id === recommendedPlanId;
                    return (
                      <th
                        key={plan.id}
                        data-testid={`comparison-col-${plan.id}`}
                        className={`px-4 py-6 text-center align-bottom ${
                          recommended ? "bg-black/[0.03]" : ""
                        }`}
                      >
                        {recommended && (
                          <div className="mb-2 inline-block rounded-[6px] bg-black px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white">
                            Recommended
                          </div>
                        )}
                        <div className="text-sm font-semibold text-black">{plan.name}</div>
                        <div className="mt-1 text-lg font-black tracking-tight text-black">
                          {pricing.label}
                          {pricing.sub && (
                            <span className="ml-0.5 text-xs font-light text-black/50">{pricing.sub}</span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {CAPABILITY_GROUPS.map((g) => (
                  <Fragment key={`g-${g.group}`}>
                    <GroupRow label={g.group} />
                    {g.items.map((item) => (
                      <FeatureRow
                        key={`${g.section}-${item}`}
                        label={featureLabel(item)}
                        values={plans.map((p) => hasFeature(p, g.section, item))}
                      />
                    ))}
                  </Fragment>
                ))}

                <GroupRow label="Review sites" />
                {REVIEW_SITES.map((site) => (
                  <FeatureRow
                    key={`site-${site}`}
                    label={platformName(site)}
                    values={plans.map((p) => hasSite(p, site))}
                  />
                ))}

                <GroupRow label="Listings" />
                {LISTINGS.map((l) => (
                  <FeatureRow
                    key={`list-${l}`}
                    label={platformName(l)}
                    values={plans.map((p) => hasListing(p, l))}
                  />
                ))}

                <GroupRow label="Social platforms" />
                {SOCIAL.map((s) => (
                  <FeatureRow
                    key={`soc-${s}`}
                    label={platformName(s)}
                    values={plans.map((p) => hasSocial(p, s))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
